import React, { useState } from 'react';
import { NavLink, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Components
import { LinkText } from '@styled';

// Functionality
import { mediaQueries } from '@general/breakpoints';
import colors from '@general/colors';
import fonts from '@general/fonts';
import { useScreenMdDown, useScreenMinLg } from '@hooks/useResponsive';

// show mobile view for phones & tablets
const breakpoint = mediaQueries.lg;

const SidebarItemStyled = styled.li`
    font-size: 15px;
    border-left: none;
    text-align: center;
    border-bottom: 5px solid transparent;
    margin: 0 14px;
    min-width: 70px;
    position: relative;

    @media ${breakpoint} {
        border-left: 8px solid transparent;
        border-bottom: none;
        text-align: left;
        margin: 0%;
    }

    &:hover,
    &.active,
    &.active:hover {
        background-color: transparent;
        border-bottom: 5px solid ${colors.primary};

        @media ${breakpoint} {
            border-bottom: none;
            border-left: 8px solid ${colors.primary};
            background-color: ${colors.azureTint};
        }

        a {
            color: ${colors.black};
        }
    }

    &:hover {
        @media ${breakpoint} {
            border-left: 8px solid transparent;
            background-color: ${colors.lightGray};
        }
    }

    a {
        color: ${colors.darkGray};

        @media ${breakpoint} {
            color: ${colors.black};
        }

        &:hover {
            color: ${colors.black};
        }
    }
`;

const SidebarInner = styled.span`
    display: flex;
    padding-top: 18px;
    padding-bottom: 8px;
    padding-left: 0;
    padding-right: 0;
    line-height: 1.2;
    flex-direction: column;
    justify-content: center;

    @media ${breakpoint} {
        flex-direction: row;
        height: 70px;
        padding-top: 0;
        padding-left: 1.4rem;
        padding-bottom: 0;
        padding-right: 10px;
        justify-content: start;
        align-items: center;
    }

    span {
        height: 20px;
        margin-right: 0;

        @media ${breakpoint} {
            height: auto;
            margin-right: 15px;
        }
    }

    svg {
        width: 20px;
        height: 20px;
        @media ${breakpoint} {
            width: 40px;
            height: 40px;
        }
    }
`;

export const BadgeStyled = styled.div`
    text-align: center;
    background-color: ${colors.azure};
    border-radius: 100%;
    font-family: ${fonts.sans};
    color: ${colors.white};
    padding: 0;
    font-size: 0.8rem;
    font-weight: 700;
    position: absolute;
    right: 32px;
    top: 24px;
    height: 26px;
    width: 26px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

// Subnav Component
const Subnav = styled.ul`
    padding-left: 20px;
    display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};

    li {
        margin: 5px 0;
        a {
            font-size: 14px;
            color: ${colors.darkGray};
        }
    }
`;

const SidebarItem = ({ label, mobileLabel, to, activeOnlyWhenExact, icon, badgeText, children }) => {
    const isSmallScreen = useScreenMdDown();
    const isDesktop = useScreenMinLg();
    const [isSubnavVisible, setSubnavVisible] = useState(false); // State for toggle
    const Icon = icon;

    const match = useRouteMatch({
        path: to,
        exact: activeOnlyWhenExact,
    });

    const handleToggleSubnav = () => {
        setSubnavVisible(prev => !prev);
    };

    return (
        <SidebarItemStyled className={match ? 'active' : ''}>
            <NavLink to={to} onClick={handleToggleSubnav}>
                <SidebarInner>
                    <span className="mb-1 mb-md-0">
                        <Icon isActive={!!match} />
                    </span>

                    {isSmallScreen && mobileLabel && mobileLabel}
                    {(isDesktop || !mobileLabel) && <LinkText className="mb-0.25rem">{label}</LinkText>}
                    {isDesktop && badgeText && <BadgeStyled>{badgeText}</BadgeStyled>}
                </SidebarInner>
            </NavLink>
            {children && (
                <Subnav isVisible={isSubnavVisible}>
                    {children}
                </Subnav>
            )}
        </SidebarItemStyled>
    );
};

SidebarItem.propTypes = {
    label: PropTypes.string.isRequired,
    mobileLabel: PropTypes.string,
    to: PropTypes.string,
    activeOnlyWhenExact: PropTypes.bool,
    icon: PropTypes.func,
    badgeText: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    children: PropTypes.node, // for third-level items
};

const SidebarItemExternal = ({ label, target = '_self', to, icon }) => {
    const Icon = icon || null;
    return (
        <SidebarItemStyled>
            <a href={to} target={target}>
                <SidebarInner>
                    {Icon && (
                        <span className="mb-1 mb-md-0">
                            <Icon />
                        </span>
                    )}
                    <LinkText className="mb-0.25rem">{label}</LinkText>
                </SidebarInner>
            </a>
        </SidebarItemStyled>
    );
};

SidebarItemExternal.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.func,
    target: PropTypes.string,
    to: PropTypes.string,
};

export { SidebarItem, SidebarItemExternal };
